import {
  IDatabaseService,
  IDatabaseAdapter,
  DatabaseInitOptions,
  IRepositoryFactory,
  type DatabaseMigrationProgress,
} from '@app/domain/ports';
import { ReplaySubject } from 'rxjs';
import { SqliteProvider } from '../providers/sqlite.provider';
import { createSqliteProvider } from '../factories/sqlite-provider.factory';
import { DefaultRepositoryFactory } from '../factories/default-repository.factory';
import { validateSqliteMode } from '../utils/detect-sqlite-mode.utils';
import { SqliteConfigurationError } from '../../errors';

type MigrationsModule = typeof import('../migrations');

let migrationsModulePromise: Promise<MigrationsModule> | null = null;

const loadMigrationsModule = async (): Promise<MigrationsModule> => {
  if (!migrationsModulePromise) {
    migrationsModulePromise = import('../migrations');
  }

  return migrationsModulePromise;
};

/**
 * Database service adapter that implements IDatabaseService port.
 * Encapsulates SQLite provider, migrations, and repository factory.
 *
 * Note: This is NOT an @Injectable service. It is registered manually in DI
 * via DATABASE_SERVICE token to maintain proper port-adapter separation.
 */
export class DatabaseService implements IDatabaseService, IDatabaseAdapter {
  private sqliteProvider: SqliteProvider | undefined;
  private readonly repositoryFactory: DefaultRepositoryFactory;
  private readonly sqliteMode: 'straight' | 'worker' | 'opfs' | 'native';
  // Keep the full migration lifecycle available for late subscribers during startup.
  private readonly migrationProgressSubject = new ReplaySubject<DatabaseMigrationProgress>();
  private initializationPromise: Promise<void> | null = null;
  readonly migrationProgress$ = this.migrationProgressSubject.asObservable();

  constructor(options: { bankRequestCacheTtlMs: number; sqliteMode?: 'straight' | 'worker' | 'opfs' | 'native' }) {
    // Create repository factory immediately to allow IndexedDB and localStorage repositories
    // to be used before SQLite initialization
    this.repositoryFactory = new DefaultRepositoryFactory(options);
    this.sqliteMode = options.sqliteMode || 'straight';
  }

  async initialize(options: DatabaseInitOptions): Promise<void> {
    if (this.isInitialized()) {
      // Re-initialization — used during user registration; always proceeds.
      await this.sqliteProvider?.close();
      this.sqliteProvider = undefined;
    } else if (this.initializationPromise) {
      // Concurrent call while initialization is already in progress (e.g. backup
      // restore triggers a reactive re-init before the first one completes).
      // Return the same promise so migrations only run once.
      return this.initializationPromise;
    }

    this.initializationPromise = this.runInitialization(options);
    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async runInitialization(options: DatabaseInitOptions): Promise<void> {
    // 1. Validate that the requested mode is supported (throws if OPFS unavailable)
    validateSqliteMode(this.sqliteMode);
    console.info(`[DatabaseService] Initializing with ${this.sqliteMode} mode`);

    // 2. Create SQLite provider with validated mode
    this.sqliteProvider = await createSqliteProvider({
      mode: this.sqliteMode,
      data: options.data,
    });

    // 3. Run migrations (automatic by default)
    if (options.runMigrations !== false) {
      await this.runMigrations();
    }

    // 4. Set SQLite provider in the factory
    this.repositoryFactory.setSqliteProvider(this.sqliteProvider);
  }

  isInitialized(): boolean {
    return this.sqliteProvider !== undefined;
  }

  async getDatabaseVersion(): Promise<number> {
    if (!this.sqliteProvider) throw new SqliteConfigurationError('Database not initialized. Call initialize() first.');

    const migrations = await loadMigrationsModule();
    const migrationManager = new migrations.MigrationManager(this.sqliteProvider);
    await migrationManager.initialize();
    return await migrationManager.getCurrentVersion();
  }

  getRepositoryFactory(): IRepositoryFactory {
    return this.repositoryFactory;
  }

  async importDatabase(data: Uint8Array): Promise<void> {
    if (!this.sqliteProvider) throw new SqliteConfigurationError('Database not initialized. Call initialize() first.');

    await this.sqliteProvider.import(data);
    // Apply pending migrations on restored data before stores reload.
    await this.runMigrations();
    // Notify the ChangeTracker so that DatabaseSyncService persists the restored
    // database to IndexedDB. importDatabase() calls the raw sqliteProvider directly,
    // bypassing the trackedProxy in DefaultRepositoryFactory, so we must mark dirty
    // explicitly here to ensure the sync pipeline fires.
    this.repositoryFactory.getChangeTracker().markDirty();
  }

  async exportDatabase(): Promise<Uint8Array> {
    if (!this.sqliteProvider) throw new SqliteConfigurationError('Database not initialized. Call initialize() first.');

    return await this.sqliteProvider.export();
  }

  async close(): Promise<void> {
    if (this.sqliteProvider) {
      await this.sqliteProvider.close();
      this.sqliteProvider = undefined;
    }
  }

  async executeRawQuery(sql: string, params?: unknown[]): Promise<unknown[]> {
    if (!this.sqliteProvider) throw new SqliteConfigurationError('Database not initialized. Call initialize() first.');

    return await this.sqliteProvider.execute(sql, params);
  }

  /**
   * Run pending database migrations
   * @private Internal method called during initialization
   */
  private async runMigrations(): Promise<void> {
    if (!this.sqliteProvider) throw new SqliteConfigurationError('SqliteProvider not initialized');

    const migrations = await loadMigrationsModule();
    const currentVersion = await migrations.runAllMigrations(this.sqliteProvider, {
      onQueued: ({ total, completedCount }) => {
        this.migrationProgressSubject.next({
          status: 'queued',
          total,
          completedCount,
        });
      },
      onMigrationStarted: ({ total, completedCount, currentIndex, migration }) => {
        this.migrationProgressSubject.next({
          status: 'running',
          total,
          completedCount,
          currentIndex,
          currentVersion: migration.version,
          currentName: migration.name,
        });
      },
      onCompleted: ({ total, completedCount, durationMs }) => {
        this.migrationProgressSubject.next({
          status: 'completed',
          total,
          completedCount,
          durationMs,
        });
      },
      onFailed: ({ total, completedCount, currentIndex, migration, error }) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorDetails = this.buildErrorDetails(error);

        this.migrationProgressSubject.next({
          status: 'failed',
          total,
          completedCount,
          currentIndex,
          currentVersion: migration.version,
          currentName: migration.name,
          errorMessage,
          errorDetails,
        });
      },
    });
    console.log(`✓ Database initialized. Current version: ${currentVersion}`);
  }

  private buildErrorDetails(error: unknown): string | undefined {
    if (!(error instanceof Error)) {
      return String(error);
    }

    const parts: string[] = [`${error.name}: ${error.message}`];

    if (error.cause) {
      if (error.cause instanceof Error) {
        parts.push(`Cause: ${error.cause.name}: ${error.cause.message}`);
        if (error.cause.stack) {
          parts.push(`Cause stack:\n${error.cause.stack}`);
        }
      } else {
        parts.push(`Cause: ${String(error.cause)}`);
      }
    }

    if (error.stack) {
      parts.push(`Stack:\n${error.stack}`);
    }

    return parts.join('\n\n');
  }
}
